import { useEffect, useState } from 'react';
import type {
  AuditEntry,
  CorrectionView,
  DashboardResponse,
  ProductView,
  VialView,
} from '@medistock/contracts';
import { ApiError, api, localTime, money } from '../api';

export function Dashboard({ onNotice }: { onNotice: (m: string) => void }) {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .dashboard()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  }, []);

  if (error) return <p className="notice error">{error}</p>;
  if (!data) return <p className="notice info">Loading…</p>;

  return (
    <div className="list" data-testid="dashboard">
      <div className="stats">
        <div className="stat">
          <div className="k">Usage cost (30d)</div>
          <div className="v" data-testid="stat-usage">
            {money(data.netUsageCostCents)}
          </div>
        </div>
        <div className="stat">
          <div className="k">Waste cost (30d)</div>
          <div className="v" data-testid="stat-waste">
            {money(data.wasteCostCents)}
          </div>
        </div>
        <div className="stat">
          <div className="k">Shrinkage (30d)</div>
          <div className="v" data-testid="stat-shrinkage">
            {money(data.shrinkageCostCents)}
          </div>
        </div>
        <div className="stat">
          <div className="k">Awaiting approval</div>
          <div className="v" data-testid="stat-pending">
            {data.pendingCorrections}
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Low stock</h2>
        {data.lowStock.length === 0 ? (
          <p className="small muted">Everything is above its reorder level.</p>
        ) : (
          <div className="list">
            {data.lowStock.map((l) => (
              <div className="item" key={l.productId}>
                <div className="top">
                  <span>{l.productName}</span>
                  <span className="badge pending">
                    {l.onHand} / {l.reorderLevel} {l.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Expiring soon</h2>
        {data.expiringSoon.length === 0 ? (
          <p className="small muted">No lots expiring in the warning window.</p>
        ) : (
          <div className="list">
            {data.expiringSoon.map((v) => (
              <div className="item" key={v.vialId}>
                <div className="top">
                  <span>
                    {v.productName} · lot {v.lot}
                  </span>
                  <span className="badge pending">{v.expiry}</span>
                </div>
                <div className="sub">{v.remaining} remaining</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <button className="ghost" onClick={() => onNotice('Dashboard covers the last 30 days.')}>
        About these figures
      </button>
    </div>
  );
}

export function Inventory({
  products,
  onNotice,
  reload,
}: {
  products: ProductView[];
  onNotice: (m: string) => void;
  reload: () => void;
}) {
  const [vials, setVials] = useState<VialView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<'receive' | 'count' | null>(null);
  const [countVial, setCountVial] = useState<VialView | null>(null);

  const load = () => {
    api
      .inventory()
      .then((r) => setVials(r.vials))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  };
  useEffect(load, []);

  const open = async (vialId: number) => {
    try {
      await api.open(vialId);
      onNotice('Vial opened and ready for staff.');
      load();
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open the vial');
    }
  };

  return (
    <div className="list" data-testid="inventory">
      {error ? <p className="notice error">{error}</p> : null}
      <div className="row">
        <button className="primary grow" data-testid="open-receive" onClick={() => setSheet('receive')}>
          Receive stock
        </button>
      </div>

      {vials.map((v) => (
        <div className="item" key={v.id} data-testid={`vial-${v.id}`}>
          <div className="top">
            <span>
              {v.productName} · lot {v.lot}
            </span>
            <span className="badge">{v.status}</span>
          </div>
          <div className="sub">
            {v.remaining} / {v.initial} {v.unit} · expires {v.expiry} · {money(v.costTotalCents)} per vial
          </div>
          <div className="row" style={{ marginTop: 8 }}>
            {v.status === 'UNOPENED' ? (
              <button data-testid={`open-${v.id}`} onClick={() => void open(v.id)}>
                Open vial
              </button>
            ) : null}
            {v.status === 'ACTIVE' ? (
              <button
                data-testid={`count-${v.id}`}
                onClick={() => {
                  setCountVial(v);
                  setSheet('count');
                }}
              >
                End-of-day count
              </button>
            ) : null}
          </div>
        </div>
      ))}
      {vials.length === 0 ? <p className="notice info">No stock received yet.</p> : null}

      {sheet === 'receive' ? (
        <ReceiveSheet
          products={products}
          onClose={() => setSheet(null)}
          onDone={(m) => {
            setSheet(null);
            onNotice(m);
            load();
            reload();
          }}
        />
      ) : null}

      {sheet === 'count' && countVial ? (
        <CountSheet
          vial={countVial}
          onClose={() => setSheet(null)}
          onDone={(m) => {
            setSheet(null);
            onNotice(m);
            load();
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ReceiveSheet({
  products,
  onClose,
  onDone,
}: {
  products: ProductView[];
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [productId, setProductId] = useState(products[0]?.id ?? 0);
  const [lot, setLot] = useState('');
  const [expiry, setExpiry] = useState('');
  const [totalQty, setTotalQty] = useState('');
  const [cost, setCost] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.receive({
        productId,
        lot: lot.trim(),
        expiry,
        totalQty: Number(totalQty),
        costTotalUsd: Number(cost),
      });
      onDone(`Received lot ${lot.trim()} as UNOPENED.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not receive the vial');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label="Receive stock">
      <div className="panel">
        <div className="row split">
          <strong>Receive stock</strong>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="field">
          <label htmlFor="rp">Product</label>
          <select id="rp" data-testid="receive-product" value={productId} onChange={(e) => setProductId(Number(e.target.value))}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.unit})
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="rl">Lot number</label>
          <input id="rl" data-testid="receive-lot" value={lot} onChange={(e) => setLot(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="re">Expiry</label>
          <input id="re" data-testid="receive-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="rq">Total amount in the vial</label>
          <input id="rq" data-testid="receive-qty" inputMode="decimal" value={totalQty} onChange={(e) => setTotalQty(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="rc">Purchase cost (USD)</label>
          <input id="rc" data-testid="receive-cost" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
        </div>
        {error ? <p className="notice error" role="alert">{error}</p> : null}
        <button
          className="primary"
          data-testid="receive-submit"
          disabled={busy || !lot.trim() || !expiry || !totalQty || cost === ''}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : 'Receive as unopened'}
        </button>
      </div>
    </div>
  );
}

function CountSheet({
  vial,
  onClose,
  onDone,
}: {
  vial: VialView;
  onClose: () => void;
  onDone: (m: string) => void;
}) {
  const [actual, setActual] = useState(String(vial.remaining));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const differs = Number(actual) !== vial.remaining;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.count({
        vialId: vial.id,
        actualQty: Number(actual),
        reason: reason.trim() || undefined,
      });
      onDone(`Counted ${result.actual} against an expected ${result.expected}.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the count');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet" role="dialog" aria-label="End of day count">
      <div className="panel">
        <div className="row split">
          <strong>
            Count {vial.productName} lot {vial.lot}
          </strong>
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <p className="small muted">
          Expected {vial.remaining} {vial.unit}.
        </p>
        <div className="field">
          <label htmlFor="ca">Actual amount in the vial</label>
          <input id="ca" data-testid="count-actual" inputMode="decimal" value={actual} onChange={(e) => setActual(e.target.value)} />
        </div>
        {differs ? (
          <div className="field">
            <label htmlFor="cr">Reason for the difference (required)</label>
            <textarea id="cr" data-testid="count-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        ) : null}
        {error ? <p className="notice error" role="alert">{error}</p> : null}
        <button
          className="primary"
          data-testid="count-submit"
          disabled={busy || (differs && reason.trim().length === 0)}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : 'Save count'}
        </button>
      </div>
    </div>
  );
}

export function Approvals({ onNotice }: { onNotice: (m: string) => void }) {
  const [requests, setRequests] = useState<CorrectionView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => {
    api
      .corrections('PENDING')
      .then((r) => setRequests(r.requests))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  };
  useEffect(load, []);

  const decide = async (id: number, decision: 'APPROVE' | 'REJECT') => {
    setBusyId(id);
    setError(null);
    try {
      await api.decide(id, decision);
      onNotice(decision === 'APPROVE' ? 'Approved. Inventory updated.' : 'Rejected. Nothing changed.');
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the decision');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="list" data-testid="approvals">
      {error ? <p className="notice error">{error}</p> : null}
      {requests.length === 0 ? <p className="notice info">Nothing is waiting for approval.</p> : null}
      {requests.map((r) => (
        <div className="item" key={r.id} data-testid={`request-${r.id}`}>
          <div className="top">
            <span>
              {r.original.productName} · {r.requester}
            </span>
            <span className="badge pending">{r.requestType}</span>
          </div>
          <div className="sub">
            Recorded {r.original.qty} {r.original.unit}
            {r.requestType === 'EDIT' ? ` → requested ${r.requestedQty} ${r.original.unit}` : ' → cancel'}
            {' · '}
            lot {r.original.lot} · {localTime(r.original.createdAt)}
          </div>
          <div className="sub">Reason: {r.reason}</div>
          <div className="row" style={{ marginTop: 8 }}>
            <button
              className="primary grow"
              data-testid={`approve-${r.id}`}
              disabled={busyId === r.id}
              onClick={() => void decide(r.id, 'APPROVE')}
            >
              Approve
            </button>
            <button
              className="grow"
              data-testid={`reject-${r.id}`}
              disabled={busyId === r.id}
              onClick={() => void decide(r.id, 'REJECT')}
            >
              Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Audit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .audit()
      .then((r) => setEntries(r.entries))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  }, []);

  if (error) return <p className="notice error">{error}</p>;

  return (
    <div className="card" data-testid="audit">
      <h2>Audit ledger</h2>
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Product</th>
              <th>Lot</th>
              <th>Event</th>
              <th>Change</th>
              <th>By</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.eventId}>
                <td>{localTime(e.createdAt)}</td>
                <td>{e.productName}</td>
                <td>{e.lot}</td>
                <td>{e.type}</td>
                <td>
                  {e.delta > 0 ? '+' : ''}
                  {e.delta} {e.unit}
                </td>
                <td>{e.actor ?? '—'}</td>
                <td>{e.reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {entries.length === 0 ? <p className="small muted">No ledger entries yet.</p> : null}
    </div>
  );
}
